import type {Location, RouteObject} from '@github-ui/react-router'
import {matchRoutes} from '@github-ui/react-router'
import type React from 'react'

import type {NavigatorRouteRegistration, PreloadableComponent} from './app-routing-types'

/** The only shape these helpers need from a route. */
interface RouteWithComponent {
  Component?: React.ComponentType | null
  children?: RouteWithComponent[]
}

/**
 * Collect preload promises for an already-matched route chain.
 * Each promise is wrapped with error handling to prevent cascading failures.
 * Components with ssr: false are skipped.
 *
 * This deliberately does not recurse into `route.children`: `matchRoutes` already returns the
 * full ancestor-to-leaf chain — including index, pathless-layout, and splat children — so
 * recursing would additionally preload every *unmatched* sibling branch, importing chunks that
 * will never render and blocking first paint on them.
 */
function collectPreloadPromises(routes: RouteWithComponent[]): Array<Promise<unknown>> {
  return routes.flatMap(route => {
    const component = route.Component as PreloadableComponent | undefined | null
    const shouldPreload = component?.preload && component.ssr !== false
    return shouldPreload ? [preloadComponentWithErrorHandling(component)] : []
  })
}

/**
 * Preload a component with error handling.
 * Silently catches errors to prevent individual failures from blocking SSR or client rendering.
 */
async function preloadComponentWithErrorHandling(component: {preload?: () => Promise<unknown>}): Promise<void> {
  try {
    await component.preload?.()
  } catch {
    // Silently ignore preload failures - the component will load on demand
  }
}

/**
 * Preloads components for routes that match the initial location.
 * Uses graceful error handling to ensure individual component failures don't break the entire operation.
 */
export async function preloadMatchedRouteComponents(
  routes: NavigatorRouteRegistration[],
  initialLocation: Location<unknown>,
): Promise<void> {
  const matches = matchRoutes(routes, initialLocation.pathname)
  if (!matches || matches.length === 0) return

  await Promise.allSettled(collectPreloadPromises(matches.map(match => match.route)))
}

/**
 * Preloads components for data router routes that match the given path.
 * Uses graceful error handling to ensure individual component failures don't break the entire operation.
 */
export async function preloadMatchedDataRouterComponents(routes: RouteObject[], pathname: string): Promise<void> {
  const matches = matchRoutes(routes, pathname)
  if (!matches || matches.length === 0) return

  await Promise.allSettled(collectPreloadPromises(matches.map(match => match.route)))
}
